import {
  type AccountDetail,
  AccountDetailSchema,
  type AccountsListResponse,
  type ChannelSettingsUpdateBody,
  ChannelSettingsUpdateBodySchema,
  type MediaRequirements
} from '@schemas/account'
import { type AgentLastTickResponse, AgentLastTickResponseSchema } from '@schemas/agent-last-tick'
import type { DeferralReason, WakeOption } from '@schemas/agent-scheduling'
import {
  type AnalysisUploadResponse,
  AnalysisUploadResponseSchema,
  type AssetStatus,
  type AssetTagCounts,
  AssetTagCountsSchema,
  type FinalizeUpload,
  type FinalizeUploadResponse,
  FinalizeUploadResponseSchema,
  type InitUploadResponse,
  InitUploadResponseSchema,
  SetAssetCampaignsRequestSchema
} from '@schemas/asset'
import {
  type AssetAnalysisResponse,
  AssetAnalysisResponseSchema,
  type AssetAnalysisRetryResponse,
  AssetAnalysisRetryResponseSchema
} from '@schemas/asset-analysis'
import {
  type AcceptAssetRequest,
  AcceptAssetRequestSchema,
  type AcceptAssetResponse,
  AcceptAssetResponseSchema,
  type AssetDetailResponse,
  AssetDetailResponseSchema,
  type AssetProjection,
  type AssetsListResponse,
  AssetsListResponseSchema,
  CategoryAmendmentRequestSchema,
  ManualAssetResolutionInputSchema
} from '@schemas/asset-triage'
import {
  type CampaignCreateBody,
  CampaignCreateBodySchema,
  type CampaignDraftResponse,
  CampaignDraftResponseSchema,
  type CampaignHandoff,
  type CampaignHandoffPreview,
  CampaignHandoffPreviewSchema,
  type CampaignUpdateBody as CampaignLifecycleUpdateBody,
  CampaignListResponseSchema,
  type CampaignPurpose,
  CampaignResponseSchema,
  type CampaignRow,
  type CampaignSettingsView,
  type CampaignStartResponse,
  CampaignStartResponseSchema,
  CampaignUpdateBodySchema,
  type PublishingMode
} from '@schemas/campaign'
import type { ChangelogEntryRow, ChangelogListResponse } from '@schemas/changelog'
import { type CreatorInfoResponse, CreatorInfoResponseSchema } from '@schemas/creator-info'
import {
  DeliveryRescheduleBodySchema,
  DeliveryResponseSchema,
  type DeliveryRetryBody,
  DeliveryRetryBodySchema,
  type DeliveryRow
} from '@schemas/delivery'
import type { RecentMetricsResponse } from '@schemas/metrics'
import {
  type ApproveProposalBody,
  ProposalCancelBodySchema,
  type ProposalCancelResponse,
  ProposalCancelResponseSchema,
  type ProposalDetailResponseInput,
  ProposalDetailResponseSchema,
  type ProposalLaneSchema,
  ProposalLanesUpdateBodySchema,
  type ProposalListItemResponse,
  type ProposalListItemResponseInput,
  type ProposalStatus,
  type ProposalSystemError,
  type ProposalsListResponseInput,
  ProposalsListResponseSchema
} from '@schemas/proposal'
import type { AccountRecommendations, CitedPost, Recommendation, VideoEvidenceState } from '@schemas/recommendation'
import {
  type InvitationPreviewResponse,
  InvitationPreviewResponseSchema,
  type RemoveWorkspaceMemberResponse,
  RemoveWorkspaceMemberResponseSchema,
  type WorkspaceMemberView
} from '@schemas/workspace'
import type { z } from 'zod'

export type {
  AcceptAssetRequest,
  AcceptAssetResponse,
  AccountDetail,
  AccountRecommendations,
  AccountsListResponse,
  AgentLastTickResponse,
  ApproveProposalBody,
  AssetAnalysisResponse,
  AssetAnalysisRetryResponse,
  AssetDetailResponse,
  AssetProjection,
  AssetStatus,
  AssetsListResponse,
  CampaignDraftResponse,
  CampaignPurpose,
  CampaignSettingsView,
  ChangelogEntryRow,
  ChangelogListResponse,
  CitedPost,
  MediaRequirements,
  ProposalStatus,
  ProposalSystemError,
  PublishingMode,
  RecentMetricsResponse,
  Recommendation,
  VideoEvidenceState
}

export type ApiError = { error: { code: string; message: string; details?: unknown } }

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError
  ) {
    super(body.error.message)
  }
}

// Module-level token getter, set by <AuthSync> once Clerk resolves a session — mirrors
// music-league's api.ts. Kept as a plain function reference rather than routing auth
// through React context so this fetch wrapper stays usable outside components.
let getTokenFn: (() => Promise<string | null>) | null = null

export function setTokenGetter(getter: (() => Promise<string | null>) | null): void {
  getTokenFn = getter
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getTokenFn ? await getTokenFn() : null
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {})
    }
  })
  if (!res.ok) {
    const body = (await res
      .json()
      .catch(() => ({ error: { code: 'http_error', message: res.statusText } }))) as ApiError
    throw new ApiRequestError(res.status, body)
  }
  // DELETE /api/campaigns/:id answers 204 with no body.
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' })
}

// Response type re-exports mirroring the server's shapes. Keep these in sync with the
// API layer (no code-gen for MVP; small enough to hand-maintain — see
// apps/server/src/routes/api/*.ts for the source of truth).

export type SetupStep = 'kind' | 'music' | 'basics' | 'pacing' | 'voice' | 'publishing' | 'exemplars' | null

// RAL-121: the session shape is the Zod schema now, not a hand-kept copy of it — the shell reads
// activeWorkspace.role/isTeam off it and a drifting duplicate would silently type-check.
export type { AccountSummary, SessionResponse, WorkspaceSummary } from '@schemas/session'

export type ActivateAccountResponse = { activeAccountId: string }
export type DisconnectAccountResponse = { success: true; cancelledCount: number }
export type HandoffShownResponse = { success: true }

// GET /invites/:token (not under /api — see backend/routes/invites.ts). A discriminated union
// on `state` so the frontend renders exactly one of RAL-95's four boards per state, never a
// generic error screen.
export type InviteLookupResponse =
  | { state: 'not_found' }
  | { state: 'expired'; invitedByName: string }
  | { state: 'already_claimed'; invitedByName: string }
  | { state: 'wrong_account'; invitedByName: string; invitedEmail: string; signedInEmail: string }
  | { state: 'pending'; invitedByName: string; invitedEmail: string; seatNumber: number; totalSeats: number }

export type PromotionKind = 'creator' | 'music' | 'product' | 'brand'

export type StreamingLink = { platform: string; url: string }
export type ReleaseDate = { track: string; date: string }
export type UpcomingEvent = { name: string; date: string; url?: string }

export type MusicPromotion = {
  artist_name: string
  track_names: string[]
  release_dates: ReleaseDate[]
  streaming_links: StreamingLink[]
  upcoming_events?: UpcomingEvent[]
}

export type PromotionContext = {
  kind: PromotionKind
  music?: MusicPromotion
  summary?: string
} | null

export type BlackoutWindow = { start: string; end: string }

// RAL-106: the wizard's mega "campaign" step split into one form per RAL-96 board — each
// mirrors the same-named schema in backend/routes/api/setup-schemas.ts.
export type BasicsForm = {
  name: string
  purpose: CampaignPurpose
  startDate: number | null
  endDate: number | null
}

export type PacingForm = {
  maxPostsPerDay: number
  minHoursBetweenPosts: number
  timezone: string
  blackoutWindows: BlackoutWindow[]
}

export type VoiceForm = {
  niche: string
  voice: string
  audience: string
  mustUseHashtags: string[]
  bannedHashtags: string[]
  bannedTopics: string[]
  mentionsAllowed: boolean
}

export type PublishingForm = {
  publishingMode: PublishingMode
  confidenceThreshold: number
  postsBeforeAutoPublish: number
}

export type SourceType = 'other_creator' | 'artist_own_history' | 'aspirational'

export type SeedExemplar = {
  url: string
  source_type: SourceType
  why_it_matters?: string
}

// Mirrors setup-session.ts's SetupStep — GET/POST /api/setup never reports a "complete"
// value; a completed setup means the setup_session cookie is gone entirely, at which point
// /api/session's setupComplete flag (not this endpoint) is the source of truth. See
// routes/setup/_layout.tsx's loader, which checks that flag before ever calling /api/setup.
export type SetupNextStep = 'kind' | 'music' | 'basics' | 'pacing' | 'voice' | 'publishing' | 'exemplars'

// Mirrors backend/routes/api/setup.ts's SetupStateResponse exactly — no accountId (the setup
// session cookie never carries one, see setup-session.ts's note on why) and no exemplars (the
// wizard's last step posts them straight into Campaign.create() without round-tripping through
// the cookie, so there's nothing to resume there).
export type SetupStateResponse = {
  kind: PromotionKind | null
  music: MusicPromotion | null
  basics: BasicsForm | null
  pacing: PacingForm | null
  voice: VoiceForm | null
  publishing: PublishingForm | null
  nextStep: SetupNextStep
}

// Campaign plus active-channel settings as returned by GET/PUT /api/campaign. Media requirements
// come from the shared channel schema; the optional client fields preserve compatibility with older
// fixtures while the server always serializes their defaulted values.
type CampaignClientOverrides =
  | keyof MediaRequirements
  | 'blackoutWindows'
  | 'modelOverride'
  | 'modelOverrideBy'
  | 'modelOverrideAt'
  | 'promotionContext'
  | 'rampUpCounterSource'

export type Campaign = Omit<CampaignSettingsView, CampaignClientOverrides> &
  Partial<MediaRequirements> & {
    blackoutWindows: BlackoutWindow[]
    promotionContext: PromotionContext
    rampUpCounterSource: string
  }

// GET and PUT /api/campaign both wrap the row in a `{ campaign }` envelope (see
// backend/routes/api/campaign.ts) rather than returning it bare.
export type CampaignResponse = { campaign: Campaign }

// ---- Pause control, live limits (RAL-113) -------------------------------------------------

export type BlockingSkipReason = 'daily_cap' | 'blackout' | 'min_interval' | 'rate_limit_guard'

export type CampaignLimits = {
  publishedToday: number
  maxPostsPerDay: number
  nextSlotAvailable: number
  blackoutResumesAt: number | null
  blocked: { reason: BlockingSkipReason; nextOpportunity: number } | null
}

// Mirrors backend/routes/api/campaign.ts's GET /status. `mode` is the real, explicit
// publishingMode toggle (RAL-52/RAL-106) — not derived from the ramp-up heuristic.
export type CampaignStatusResponse = {
  paused: boolean
  pausedAt: number | null
  pausedCancelledCount: number
  scheduledCount: number
  mode: 'automatic' | 'manual'
  confidenceThreshold: number
  // RAL-96 board D's "track record" — real AgentDecision count, 0 for a campaign that's never run.
  decisionsCount: number
  // RAL-157: per-outcome tallies over the same decisions the track record counts — unattended
  // (published without an approval step), held (waiting on you), refused (Rally declined, e.g. a
  // rules gate tripped before scoring).
  unattendedCount: number
  heldCount: number
  refusedCount: number
  limits: CampaignLimits
}

export type PauseResponse = { paused: true; pausedAt: number; cancelledCount: number }
export type ResumeResponse = { paused: false }

// ---- Activity timeline (RAL-113) ----------------------------------------------------------

export type TimelineActor = 'you' | 'rally'

export type ActivityTimelineEntry = {
  id: string
  occurredAt: number
  actor: TimelineActor
  mode: string | null
  headline: string
  detail: string | null
  reversibility: string
}

export type ActivityTimelineResponse = { entries: ActivityTimelineEntry[]; lastKey?: string }

export type ProposalAsset = ProposalListItemResponse['asset']

// Mirrors schemas/proposal.ts's RejectReasonEnum — RAL-97's fixed reject-reason shortlist.
export type RejectReason = ProposalListItemResponse['rejectReason'] extends infer T ? Exclude<T, null> : never

// WakeOptionEnum lives in schemas/agent-scheduling.ts, shared with the backend.
export type { WakeOption }

// Proposals are keyed by (assetId, id) — see backend/routes/api/proposals.ts's note on
// requireAssetAndProposal — so every list item carries assetId alongside id for building the
// composite-keyed detail route (/review/:assetId/:id). publishAt/createdAt/decidedAt are raw
// ms-epoch numbers (schemas/proposal.ts's msTimestampSchema), not ISO strings.
export type ProposalListItem = ProposalListItemResponseInput
export type ProposalDetail = ProposalDetailResponseInput
export type ProposalsListResponse = ProposalsListResponseInput

export const getProposals = async (path: string): Promise<ProposalsListResponse> =>
  ProposalsListResponseSchema.parse(await api.get<unknown>(path)) as ProposalsListResponse

export const getProposalDetail = async (assetId: string, proposalId: string): Promise<ProposalDetail> =>
  ProposalDetailResponseSchema.parse(
    await api.get<unknown>(`/api/proposals/${assetId}/${proposalId}`)
  ) as ProposalDetail

export const retryProposal = async (assetId: string, proposalId: string): Promise<void> => {
  await api.post(`/api/proposals/${assetId}/${proposalId}/retry`)
}

export type RejectProposalBody = {
  reason: RejectReason
  note?: string
}

export type RejectReasonsSummaryResponse = { counts: Record<RejectReason, number>; total: number }

export type WakeOptionsResponse = Record<WakeOption, number | null>

export type SnoozeProposalBody = {
  wakeOption: WakeOption
}

export type ProposalRef = { assetId: string; id: string }

export type BatchApprovePreviewItem = ProposalListItem & {
  resolvedPublishAt: number
  postsToday: boolean
  deferredBy: DeferralReason | null
}

export type BatchApprovePreviewResponse = { items: BatchApprovePreviewItem[] }
export type BatchApproveCommitResponse = { items: ProposalListItem[] }

// Mirrors schemas/asset.ts's AssetTypeEnum / AssetRightsEnum (RAL-44) — the creator-editable
// label fields the ingest modal persists via PATCH /api/assets/:id.
export type AssetType = 'Hook' | 'Crowd' | 'Behind the scenes' | 'UGC'
export type AssetRights = 'Owned' | 'Licensed' | 'Unconfirmed'

// Mirrors schemas/asset-analysis.ts's AnalysisStatusEnum. Asset.json() already serializes this
// field (backend/models/asset.ts) — it just wasn't typed here yet. RAL-158: a rough per-asset
// hint for creator-facing display only ("has the read finished") — never trust this for
// candidate-eligibility correctness, which the real AssetAnalysis ledger (generation/attempt)
// governs (see .agents/knowledge/INDEX.md).
export type AnalysisStatus = 'pending' | 'analyzing' | 'succeeded' | 'failed'

export type AssetJSON = AssetProjection

export type DeletionTier = 1 | 2 | 3

export type DeletionImpactResponse = {
  tier: DeletionTier
  proposalCount: number
  liveProposals: Array<{ id: string; status: ProposalStatus; publishAt: number }>
}

export type DeleteAssetResponse = { tier: DeletionTier; deleted?: true; removedFromLibrary?: true }

export type InitUploadBody = {
  filename: string
  sizeBytes: number
  note?: string
  hints?: Record<string, unknown>
}

export type FinalizeUploadBody = FinalizeUpload

export const initializeUpload = async (body: InitUploadBody): Promise<InitUploadResponse> =>
  InitUploadResponseSchema.parse(await api.post<unknown>('/api/uploads/init', body))

export const requestAnalysisUpload = async (assetId: string): Promise<AnalysisUploadResponse> =>
  AnalysisUploadResponseSchema.parse(await api.post<unknown>(`/api/uploads/${assetId}/analysis-url`))

export const finalizeUpload = async (assetId: string, body: FinalizeUploadBody): Promise<FinalizeUploadResponse> =>
  FinalizeUploadResponseSchema.parse(await api.post<unknown>(`/api/uploads/finalize/${assetId}`, body))

export const getAssets = async (): Promise<AssetsListResponse> =>
  AssetsListResponseSchema.parse(await api.get<unknown>('/api/assets'))

export const getAssetTagCounts = async (): Promise<AssetTagCounts> =>
  AssetTagCountsSchema.parse(await api.get<unknown>('/api/assets/tag-counts'))

// Replaces the asset's campaign tags; an empty list untags it.
export const setAssetCampaigns = async (assetId: string, campaignIds: string[], mutationId: string): Promise<void> => {
  await api.put(`/api/assets/${assetId}/campaigns`, SetAssetCampaignsRequestSchema.parse({ campaignIds, mutationId }))
}

export const updateChannelSettings = async (
  channelId: string,
  body: ChannelSettingsUpdateBody
): Promise<AccountDetail> =>
  AccountDetailSchema.parse(
    (
      await api.put<{ account: unknown }>(
        `/api/accounts/${channelId}/settings`,
        ChannelSettingsUpdateBodySchema.parse(body)
      )
    ).account
  )

export const getAssetDetail = async (assetId: string): Promise<AssetDetailResponse> =>
  AssetDetailResponseSchema.parse(await api.get<unknown>(`/api/assets/${assetId}`))

export type AcceptAssetLabels = AcceptAssetRequest

export const getAssetAnalysis = async (assetId: string): Promise<AssetAnalysisResponse> =>
  AssetAnalysisResponseSchema.parse(await api.get<unknown>(`/api/assets/${assetId}/analysis`))

export const resolveAssetManually = async (assetId: string, body: unknown): Promise<AssetDetailResponse> =>
  AssetDetailResponseSchema.parse(
    await api.post<unknown>(
      `/api/assets/${assetId}/analysis/manual-resolution`,
      ManualAssetResolutionInputSchema.parse(body)
    )
  )

type DeliveryRef = Pick<DeliveryRow, 'assetId' | 'proposalId' | 'channelId'>

const deliveryPath = (delivery: DeliveryRef): string =>
  `/api/proposals/${delivery.assetId}/${delivery.proposalId}/deliveries/${delivery.channelId}`

// RAL-224: retries one failed lane now; the proposal's other lanes are untouched.
export const retryDelivery = async (delivery: DeliveryRef, body: DeliveryRetryBody = {}): Promise<DeliveryRow> =>
  DeliveryResponseSchema.parse(
    await api.post<unknown>(`${deliveryPath(delivery)}/retry`, DeliveryRetryBodySchema.parse(body))
  ).delivery

// Moves one scheduled or missed lane to a time the creator picked (RAL-228).
export const rescheduleDelivery = async (delivery: DeliveryRef, scheduledAt: number): Promise<DeliveryRow> =>
  DeliveryResponseSchema.parse(
    await api.patch<unknown>(deliveryPath(delivery), DeliveryRescheduleBodySchema.parse({ scheduledAt }))
  ).delivery

// RAL-237: cancels an approved proposal's scheduled lanes; no `channelIds` means every lane.
export const cancelProposalLanes = async (
  delivery: Pick<DeliveryRef, 'assetId' | 'proposalId'>,
  channelIds?: string[]
): Promise<ProposalCancelResponse> =>
  ProposalCancelResponseSchema.parse(
    await api.post<unknown>(
      `/api/proposals/${delivery.assetId}/${delivery.proposalId}/cancel`,
      ProposalCancelBodySchema.parse(channelIds ? { channelIds } : {})
    )
  )

export const updateProposalLanes = async (
  assetId: string,
  proposalId: string,
  lanes: z.input<typeof ProposalLaneSchema>[]
): Promise<void> => {
  await api.put(`/api/proposals/${assetId}/${proposalId}/lanes`, ProposalLanesUpdateBodySchema.parse({ lanes }))
}

export const getCreatorInfo = async (channelId: string): Promise<CreatorInfoResponse> =>
  CreatorInfoResponseSchema.parse(await api.get<unknown>(`/api/accounts/${channelId}/creator-info`))

// Only valid when the current analysis failed terminally (`canRetry === false`); the route
// rejects anything else with a 409. Both `AssetAnalysisRetryErrorCodeEnum` conflicts also
// arrive as a 409 — read `ApiRequestError.body.error.code` to tell them apart from a plain
// "retry is not available", which carries no structured code.
export const retryAssetAnalysis = async (assetId: string): Promise<AssetAnalysisRetryResponse> =>
  AssetAnalysisRetryResponseSchema.parse(await api.post<unknown>(`/api/assets/${assetId}/analysis/retry`))

export const overrideAssetCategory = async (
  assetId: string,
  category: unknown,
  mutationId: string
): Promise<AssetDetailResponse> =>
  AssetDetailResponseSchema.parse(
    await api.patch<unknown>(
      `/api/assets/${assetId}/analysis/category`,
      CategoryAmendmentRequestSchema.parse({ category, mutationId })
    )
  )

export const acceptAsset = async (assetId: string, labels: AcceptAssetLabels): Promise<AcceptAssetResponse> =>
  AcceptAssetResponseSchema.parse(
    await api.post<unknown>(`/api/assets/${assetId}/accept`, AcceptAssetRequestSchema.parse(labels))
  )

export type AgentGate = AgentLastTickResponse['gates'][number]
export const getAgentLastTick = async (): Promise<AgentLastTickResponse> =>
  AgentLastTickResponseSchema.parse(await api.get<unknown>('/api/agent/last-tick'))

// Mirrors backend/routes/api/campaign.ts's GET /activity — the dashboard glimpse (RAL-103)
// reads the same real timeline as the full /activity page, just a shorter page of it.
export type ActivityGlimpseResponse = { entries: ActivityTimelineEntry[] }

// Publishing ledger response types (RAL-116/RAL-100) live in schemas/ledger.ts, shared with the
// backend — see that file's LedgerRowSchema, ApprovedBySchema, MetricsStateSchema, and the
// *Enum exports for comparisons (never bare string literals). Import them from '@schemas/ledger'.

// ---- Campaign lifecycle (RAL-226) and autofill (RAL-220) -----------------------------------

// ponytail: 5 pages of 100 covers any real workspace; page further if one ever holds 500 campaigns.
const CAMPAIGN_PAGE_LIMIT = 100
const CAMPAIGN_MAX_PAGES = 5

export const listCampaigns = async (): Promise<CampaignRow[]> => {
  const campaigns: CampaignRow[] = []
  let start: string | undefined
  for (let page = 0; page < CAMPAIGN_MAX_PAGES; page++) {
    const query = new URLSearchParams({ limit: String(CAMPAIGN_PAGE_LIMIT) })
    if (start) query.set('start', start)
    const result = CampaignListResponseSchema.parse(await api.get<unknown>(`/api/campaigns?${query}`))
    campaigns.push(...result.campaigns)
    start = result.lastKey
    if (!start) break
  }
  return campaigns
}

export const draftCampaign = async (description: string): Promise<CampaignDraftResponse> =>
  CampaignDraftResponseSchema.parse(await api.post<unknown>('/api/campaigns/draft', { description }))

export const createCampaign = async (body: CampaignCreateBody): Promise<CampaignRow> =>
  CampaignResponseSchema.parse(await api.post<unknown>('/api/campaigns', CampaignCreateBodySchema.parse(body))).campaign

export const updateCampaign = async (id: string, body: CampaignLifecycleUpdateBody): Promise<CampaignRow> =>
  CampaignResponseSchema.parse(await api.put<unknown>(`/api/campaigns/${id}`, CampaignUpdateBodySchema.parse(body)))
    .campaign

export const deleteCampaign = async (id: string): Promise<void> => {
  await api.delete<unknown>(`/api/campaigns/${id}`)
}

export const startCampaign = async (id: string, handoff?: CampaignHandoff): Promise<CampaignStartResponse> =>
  CampaignStartResponseSchema.parse(await api.post<unknown>(`/api/campaigns/${id}/start`, handoff ? { handoff } : {}))

export const endCampaign = async (id: string): Promise<CampaignRow> =>
  CampaignResponseSchema.parse(await api.post<unknown>(`/api/campaigns/${id}/end`)).campaign

export const getHandoffPreview = async (campaignId?: string): Promise<CampaignHandoffPreview> =>
  CampaignHandoffPreviewSchema.parse(
    await api.get<unknown>(`/api/campaigns/handoff-preview${campaignId ? `?campaignId=${campaignId}` : ''}`)
  )

export type { CampaignRow }

// ---- Workspace invitations and members (RAL-250) -------------------------------------------

// Both shapes come from schemas/workspace.ts, so the wire contract has one definition. The
// preview is a `state` union (like InviteLookupResponse above) rather than an error status: a
// revoked, accepted, expired or unknown id all answer `not_available`, and the page renders that
// as its own board instead of a generic error screen.
export type { InvitationPreviewResponse, RemoveWorkspaceMemberResponse, WorkspaceMemberView }

export const getInvitationPreview = async (invitationId: string): Promise<InvitationPreviewResponse> =>
  InvitationPreviewResponseSchema.parse(await api.get<unknown>(`/api/invitations/${encodeURIComponent(invitationId)}`))

export const removeWorkspaceMember = async (userId: string): Promise<RemoveWorkspaceMemberResponse> =>
  RemoveWorkspaceMemberResponseSchema.parse(
    await api.delete<unknown>(`/api/workspace/members/${encodeURIComponent(userId)}`)
  )
